import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15, _descriptor16, _descriptor17, _descriptor18, _descriptor19, _descriptor20, _descriptor21, _descriptor22, _descriptor23;
import { action, computed, observable, runInAction } from 'mobx';
import api from 'core/util/api';
import Collection from 'core/model/Collection';
import { RuleTestState } from 'app/stores/interface/RuleTestState';
import $auth from 'app/stores/$auth';
import RuleState from 'app/stores/interface/RuleState';
import { isCompleteType } from '@kentik/ui-shared/types/sse';
import InterfaceClassCollection from './interfaceClass/InterfaceClassCollection';
import InterfacesDeviceCollection from './InterfacesDeviceCollection';
import InterfaceCollection from './InterfaceCollection';
import InterfaceSettings from './InterfaceSettings';
import RuleCollection from './RuleCollection';
import $setup from '../$setup';
let InterfaceClassStore = (_dec = observable.ref, _dec2 = observable.ref, _class = class InterfaceClassStore {
  constructor() {
    /**
     * Appears to be unused?
     */
    this.collection = new InterfaceClassCollection();
    /**
     * Flag indicating whether changes have been made to the rules (enabled/disabled, reordered, etc.)
     * that require the user to trigger a new backend evaluation run.
     */
    _initializerDefineProperty(this, "isRuleEvaluationRequired", _descriptor, this);
    /**
     * Flag indicating whether the primary classification data (rules, devices, aggregate counts)
     * has successfully finished its initial load from the backend.
     */
    _initializerDefineProperty(this, "isClassificationDataReady", _descriptor2, this);
    /**
     * Flag indicating whether the manual classification overrides have been fetched and processed.
     */
    _initializerDefineProperty(this, "isManualClassificationDataReady", _descriptor3, this);
    /**
     * The global total number of active interfaces that currently have no classification.
     */
    _initializerDefineProperty(this, "unclassifiedInterfaceCount", _descriptor4, this);
    _initializerDefineProperty(this, "isRefreshingCounts", _descriptor5, this);
    /**
     * The global total number of valid interfaces across all active devices in the system.
     */
    _initializerDefineProperty(this, "totalInterfaceCount", _descriptor6, this);
    /**
     * Manages the persistent, server-side state of rule evaluations.
     * Tracks the progress of background classification runs.
     */
    this.ruleState = void 0;
    /**
     * Manages the temporary, ephemeral state of a rule currently being actively tested or edited in the UI.
     * Handles the live WebSocket stream for previewing rule changes.
     */
    this.ruleTestState = void 0;
    /**
     * Collection containing all the saved classification rules configured for the company.
     */
    this.ruleCollection = new RuleCollection();
    /**
     * The primary collection of devices used to populate the main "Devices" tab.
     */
    this.deviceCollection = new InterfacesDeviceCollection();
    /**
     * Collection used to temporarily hold interface models when drilling down into a
     * specific device's matches inside the Rule Editor.
     */
    this.testResultsInterfaceCollection = new InterfaceCollection();
    /**
     * A secondary collection of devices specifically used in the Rule Editor modal.
     * This receives temporary match counts and sorts on the fly to power the live rule previews.
     */
    this.testResultsDeviceCollection = new InterfacesDeviceCollection();
    /**
     * Model representing the global interface classification settings
     * (e.g., default network boundaries, global exclusion rules).
     */
    this.settings = new InterfaceSettings();
    /**
     * Internal collection holding the flat list of all unclassified interfaces.
     * Lazy-loaded over the network only when the "Unclassified Interfaces" tab is opened.
     */
    this.allUnclassifiedInterfacesCollection = new InterfaceCollection([], {
      groupBy: 'device_name'
    });
    this.manualDeviceCollection = new InterfacesDeviceCollection();
    _initializerDefineProperty(this, "isFetchingActiveRuleInterfaces", _descriptor7, this);
    this.activeRuleInterfacesCollection = new InterfaceCollection();
    _initializerDefineProperty(this, "isFetchingDeviceInterfaces", _descriptor8, this);
    this.settingsFetchPromise = null;
    _initializerDefineProperty(this, "hasLoadedSettings", _descriptor9, this);
    _initializerDefineProperty(this, "v3TestResults", _descriptor0, this);
    _initializerDefineProperty(this, "v3IsTesting", _descriptor1, this);
    /**
     * The true backend count of ALL valid interfaces across the company, bypassing UI device filtering.
     */
    _initializerDefineProperty(this, "totalValidInterfacesCount", _descriptor10, this);
    /**
     * The true backend count of ALL classified valid interfaces across the company.
     */
    _initializerDefineProperty(this, "totalValidClassifiedCount", _descriptor11, this);
    /**
     * Cache of the raw device counts array from the backend API.
     */
    _initializerDefineProperty(this, "deviceCountsCache", _descriptor12, this);
    /**
     * Processes the raw detailed rule interfaces from the backend, flattens them, appends
     * device names, and injects match counts into the device collection models so they can be sorted.
     *
     * @param ruleInterfaces - Raw interface payload returned from the API.
     */
    _initializerDefineProperty(this, "updateRuleInterfaces", _descriptor13, this);
    this.refreshGeneration = 0;
    /**
     * Callback fired by the WebSocket listener when a temporary, unsaved rule test evaluation finishes.
     *
     * @param data - Payload indicating the completion status of the test run.
     */
    this.handleTestSuccess = data => {
      if (isCompleteType(data)) {
        this.updateFromTestResults();
      }
    };
    this.ensureSettingsLoaded = () => {
      if (this.hasLoadedSettings) {
        return Promise.resolve(this.settings);
      }
      if (this.settings.requestStatus === 'fetching' && this.settingsFetchPromise) {
        return this.settingsFetchPromise;
      }
      this.settingsFetchPromise = this.settings.fetch().then(result => {
        runInAction(() => {
          this.hasLoadedSettings = true;
          this.settingsFetchPromise = null;
        });
        return result;
      }, error => {
        runInAction(() => {
          this.settingsFetchPromise = null;
        });
        throw error;
      });
      return this.settingsFetchPromise;
    };
    this.getSetting = (settingName, defaultValue) => {
      const val = this.settings.get(settingName);
      if (val !== undefined) {
        return val;
      }
      return defaultValue;
    };
    /**
     * Initializes counts strictly for manually classified interfaces from the base database table,
     * bypassing any classification test results.
     *
     * Reuses the device list already fetched by `deviceCollection` (via `initClassificationCollections`)
     * instead of issuing a second, redundant `/api/ui/devices` request for the same data.
     */
    _initializerDefineProperty(this, "initManualClassificationCollections", _descriptor14, this);
    /**
     * New version of testClassify, this uses a single API call to do the entire test
     */
    _initializerDefineProperty(this, "testClassifyV3", _descriptor15, this);
    /**
     * Expands or collapses the detailed interface view for a given device.
     * Automatically intercepts live WebSocket preview data if active, or falls back to querying the DB.
     *
     * @param device - The Device model to toggle details for.
     */
    _initializerDefineProperty(this, "toggleDeviceInterfaceDetails", _descriptor16, this);
    /**
     * Callback fired when a full backend IC process completes.
     * Triggers an update of all system counts and clears caches so new data is fetched.
     */
    this.handleSuccess = data => {
      if (isCompleteType(data)) {
        Promise.all([this.getDeviceInterfaceCounts(), this.getInterfacesMatchedPerRule()]).then(([deviceCounts, ruleCounts]) => {
          this.updateDeviceInterfaceCounts(deviceCounts);
          this.updateRuleCounts(ruleCounts);
          this.allUnclassifiedInterfacesCollection.hasFetched = false;
          this.ruleState?.setEvaluationComplete(true);
        });
      }
    };
    /**
     * Persists rule state, triggering a full evaluation of all active rules against all devices.
     */
    _initializerDefineProperty(this, "evaluateRules", _descriptor17, this);
    /**
     * Tests a single rule. `values` are the new values we want to test, and have not yet
     * been persisted to `rule: BaseModel`
     *
     * @param rule - The rule being tested.
     * @param values - The new/unsaved attributes of the rule being previewed.
     */
    _initializerDefineProperty(this, "testRule", _descriptor18, this);
    /**
     * Changes the rank/priority of a rule and posts the reorder to the backend.
     *
     * @param oldIndex - The original index of the rule in the collection.
     * @param newIndex - The new desired index.
     */
    _initializerDefineProperty(this, "reorderRule", _descriptor19, this);
    /**
     * Forges a new unsaved rule and makes it the active test rule so it can be edited.
     *
     * @param options - Options payload, normally configuring starting rank.
     */
    this.addRule = (options = {}) => {
      const {
        isSettingRank = false,
        rank = this.ruleCollection.models.length
      } = options;
      const rule = this.ruleCollection.forge({
        isSettingRank,
        rank
      });
      this.setActiveTestRule(rule);
    };
    /**
     * Permanently deletes a rule from the backend and flags an evaluation run if needed.
     *
     * @param rule - The rule to remove.
     */
    _initializerDefineProperty(this, "removeRule", _descriptor20, this);
    /**
     * Prepares a rule to be tested/edited by safely cloning it into the temporary testing state.
     *
     * @param {RuleModel} rule - The rule model to isolate for testing.
     */
    _initializerDefineProperty(this, "setActiveTestRule", _descriptor21, this);
    /**
     * Flips the enabled/disabled state of a model
     *
     * @param rule - The rule to toggle.
     */
    _initializerDefineProperty(this, "toggleRuleEnabled", _descriptor22, this);
    /**
     * Submits explicit, manual interface classification overrides directly to the backend.
     *
     * @param data - Payload representing the classification override.
     */
    _initializerDefineProperty(this, "classifyInterfaces", _descriptor23, this);
    this.autoClassify = ({
      company_id,
      device_id
    }) => api.get('/api/ui/interfaces/classify/auto', {
      query: {
        company_id,
        device_id
      }
    }).then(results => {
      const devices = results.devices.map(device => ({
        ...device,
        id: device.device_id,
        results: new Collection(device.results, {
          sortState: {
            field: 'autoClassified',
            direction: 'desc'
          }
        })
      }));
      return {
        ...results,
        devices: new Collection(devices, {
          sortState: {
            field: 'autoClassifiedCount',
            direction: 'desc'
          }
        })
      };
    });
  }
  /**
   * Fetches the interfaces belonging to a particular rule (because of the rest of $interfaceClass this is expected to
   * be the "active" rule in view)
   */
  async fetchActiveRuleInterfaces(ruleId) {
    this.isFetchingActiveRuleInterfaces = true;
    this.activeRuleInterfacesCollection.reset();
    try {
      if (this.isV3Enabled) {
        this.updateRuleInterfacesV3(await api.get(`/api/ui/interfaceClassification/rules/${ruleId}/devices`));
      } else {
        const results = await this.getDetailedRuleInterfaces(ruleId);
        this.updateRuleInterfaces(results);
      }
    } finally {
      this.isFetchingActiveRuleInterfaces = false;
    }
  }
  updateRuleInterfacesV3(deviceCounts) {
    const countsMap = deviceCounts.reduce((acc, dc) => {
      acc[dc.deviceId] = dc;
      return acc;
    }, {});
    this.testResultsDeviceCollection.each(device => {
      const dc = countsMap[device.id];
      if (dc) {
        const totalInterfaces = device.get('interfaceCount') || 1;
        device.safeSet({
          activeRuleMatchCount: dc.matchedCount,
          interfacesClassifiedCount: dc.classifiedInterfaces,
          percentMatched: dc.classifiedInterfaces / totalInterfaces
        });
      } else {
        device.set('activeRuleMatchCount', 0);
      }
    });
  }
  initialize() {
    this.ruleState = new RuleState({
      onSuccess: this.handleSuccess,
      deviceCollection: this.deviceCollection,
      ruleCollection: this.ruleCollection,
      settings: this.settings
    });
    this.ruleTestState = new RuleTestState({
      onSuccess: this.handleTestSuccess,
      deviceCollection: this.testResultsDeviceCollection,
      settings: this.settings
    });
  }
  completeRefresh(currentGeneration) {
    if (currentGeneration === this.refreshGeneration) {
      this.isRefreshingCounts = false;
    }
  }

  /**
   * Gets a generation token, to ensure that we have the latest state for processing refreshes (averting race conditions)
   */
  openRuleUpdate() {
    this.refreshGeneration += 1;
    return this.refreshGeneration;
  }
  async refreshAllClassificationData(currentGeneration) {
    if (!this.isV3Enabled) {
      return Promise.resolve();
    }
    if (!currentGeneration) {
      currentGeneration = this.openRuleUpdate();
    } else if (currentGeneration !== this.refreshGeneration) {
      return Promise.resolve();
    }
    this.isRefreshingCounts = true;
    try {
      await this.ruleCollection.fetch({
        force: true
      });
    } catch (e) {
      console.error(e);
      this.completeRefresh(currentGeneration);
      return Promise.resolve();
    }
    if (currentGeneration !== this.refreshGeneration) {
      return Promise.resolve();
    }
    const promises = [
    // Refresh Device & Global Counts
    this.getDeviceInterfaceCounts().then(counts => currentGeneration === this.refreshGeneration && this.updateDeviceInterfaceCounts(counts)),
    // Refresh Rule Counts
    this.getInterfacesMatchedPerRule().then(ruleCounts => currentGeneration === this.refreshGeneration && this.updateRuleCounts(ruleCounts)),
    // Ping $setup to update the global warning banners across the app
    $setup.fetchInterfacesClassified()];

    // Refresh the Unclassified list, but only if they have already loaded it
    if (this.allUnclassifiedInterfacesCollection.hasFetched) {
      runInAction(() => {
        this.allUnclassifiedInterfacesCollection.hasFetched = false;
      });
      promises.push(this.fetchUnclassifiedInterfacesData());
    }

    // Wait for all fetches to finish (resolves or rejects) before dropping the spinners
    return Promise.all(promises).catch(e => console.error(e)).finally(() => this.completeRefresh(currentGeneration));
  }
  /**
   * Intercepts live WebSocket evaluation results from an unsaved test rule, computes
   * device and classification matches, and immediately forces UI collections to update
   * for a real-time preview experience.
   *
   * TODO I don't think it actually _is_ realtime right now though and anyway that might be too expensive to run
   */
  updateFromTestResults() {
    if (!this.ruleTestState?.rule) {
      return;
    }
    const testRule = this.ruleTestState.rule;
    const ruleId = testRule.id || testRule.optimisticId;
    const matchedInterfaces = [];
    const matchCountsByDevice = {};
    const classifiedCountsByDevice = {};
    let globalTestClassifiedCount = 0;

    // evaluationResults contains the temporary WebSocket results streamed from the backend
    this.ruleTestState.evaluationResults?.forEach(result => {
      const {
        device_id,
        interfaces = []
      } = result;
      let deviceMatchCount = 0;
      let deviceClassifiedCount = 0;
      interfaces.forEach(intf => {
        // Determine the total number of devices/interfaces classified
        const isClassified = intf.connectivity_type !== intf.initial_connectivity_type && intf.network_boundary !== intf.initial_network_boundary || intf.rules_matched?.some(rule => rule.didClassify);
        if (isClassified) {
          deviceClassifiedCount += 1;
        }

        // Determine whether this specific interface was classified by this specific rule
        const matchedOurRule = intf.rules_matched?.some(r => String(r.id) === String(ruleId) || testRule.optimisticId && String(r.id) === String(testRule.optimisticId));
        if (matchedOurRule) {
          deviceMatchCount += 1;
          matchedInterfaces.push({
            ...intf,
            device_id,
            device_name: this.deviceCollection.deviceIdNameHash[device_id]
          });
        }
      });
      globalTestClassifiedCount += deviceClassifiedCount;
      matchCountsByDevice[device_id] = deviceMatchCount;
      classifiedCountsByDevice[device_id] = deviceClassifiedCount;
    });
    this.activeRuleInterfacesCollection.set(matchedInterfaces);

    // Update the device models
    this.testResultsDeviceCollection.each(device => {
      const matched = matchCountsByDevice[device.id] || 0;
      const classified = classifiedCountsByDevice[device.id] || 0;
      const total = device.get('interfaceCount') || 1; // Prevent NaN / 0

      device.set({
        activeRuleMatchCount: matched,
        interfacesClassifiedCount: classified,
        percentMatched: classified / total
      });
    });
    if (this.totalInterfaceCount > 0) {
      this.ruleTestState.totalPercentClassified = globalTestClassifiedCount / this.totalInterfaceCount;
    } else {
      this.ruleTestState.totalPercentClassified = 0;
    }
    testRule.set('interfacesClassifiedCount', matchedInterfaces.length);
    this.testResultsDeviceCollection.sort();
  }

  /**
   * The primary initialization flow. Kicks off fetches for devices, rules, settings, and aggregate
   * UI counts. Sorts collections when finished and checks if rule evaluations are required.
   *
   * @param deviceId - Optional device ID to automatically open its detail view upon load.
   */
  initClassificationCollections(deviceId) {
    this.isClassificationDataReady = false;

    // Shared between deviceCollection and manualDeviceCollection so we only issue a single
    // `/api/ui/devices` request instead of two identical ones.
    const devicesPromise = this.deviceCollection.fetch();
    Promise.all([devicesPromise, this.getDeviceInterfaceCounts(), this.getInterfacesMatchedPerRule(), this.initManualClassificationCollections(devicesPromise), this.ruleCollection.fetch(), this.ensureSettingsLoaded()]).then(([devices, deviceCounts, ruleCounts]) => {
      this.deviceCollection.filter(undefined, {
        customFilter: model => model.get('device_status') === 'V'
      });
      // @ts-expect-error TODO this will throw an error if deviceCollection has no entries!
      this.testResultsDeviceCollection.set(this.testResultsDeviceCollection.deserialize(devices));
      this.updateDeviceInterfaceCounts(deviceCounts);
      this.updateRuleCounts(ruleCounts);
      this.isClassificationDataReady = true;
      if (!this.isV3Enabled && this.ruleCollection.size > 0 && deviceCounts.length === 0) {
        this.evaluateRules();
      } else {
        this.ruleState?.setEvaluationComplete(true);
      }
      if (deviceId) {
        const device = this.deviceCollection.get(deviceId);
        if (device) {
          this.toggleDeviceInterfaceDetails(device);
        }
      }
    });
  }
  get excludeNoDescriptionEnabled() {
    return !!this.getSetting('include_not_no_description_only', false);
  }
  get excludeNoIpEnabled() {
    return !!this.getSetting('excl_no_ip', false);
  }
  /**
   * Processes the device interface counts i.e. the number of classified devices associated with each device.
   */
  updateDeviceInterfaceCounts(deviceCounts = []) {
    this.deviceCountsCache = deviceCounts;
    let uiTotal = 0;
    let uiClassified = 0;
    let globalTotal = 0;
    let globalClassified = 0;
    deviceCounts.forEach(dc => {
      const dcTotal = parseInt(String(dc.total_interfaces), 10) || 0;
      const dcClassified = parseInt(String(dc.classified_interfaces), 10) || 0;
      globalTotal += dcTotal;
      globalClassified += dcClassified;
      const device = this.deviceCollection.get(dc.device_id);
      if (device) {
        uiTotal += dcTotal;
        uiClassified += dcClassified;
        [this.deviceCollection, this.testResultsDeviceCollection].forEach(collection => {
          collection.get(dc.device_id)?.set({
            interfaceCount: dcTotal,
            interfacesClassifiedCount: dcClassified,
            percentMatched: parseFloat(String(dc.classified_ratio || '0'))
          });
        });
      }
    });

    // sort criteria just updated, we need to sort again
    this.deviceCollection.sort();
    this.testResultsDeviceCollection.sort();
    this.totalInterfaceCount = uiTotal;
    this.unclassifiedInterfaceCount = uiTotal - uiClassified;
    this.totalValidInterfacesCount = globalTotal;
    this.totalValidClassifiedCount = globalClassified;
    this.allUnclassifiedInterfacesCollection.hasFetched = false;
    if (this.ruleState) {
      this.ruleState.ruleMatchCount = uiClassified;
    }
  }

  /**
   * Updates rules with the number of interfaces classified by each rule
   */
  updateRuleCounts(ruleCounts = []) {
    // Determine the rule counts for each rule (note: only the rules which are returned by the API though!)
    const countsMap = ruleCounts.reduce((acc, rc) => {
      acc[rc.rule_id] = parseInt(String(rc.total_interfaces_count) || '0', 10);
      return acc;
    }, {});

    // Apply the counts to the ruleCollection
    // If the rule is missing from the API response (i.e. disabled or 0 matches), it defaults to 0.
    this.ruleCollection.each(rule => {
      rule.set('interfacesClassifiedCount', countsMap[rule.id] || 0);
    });
  }

  /**
   * @returns false if not exceeding the limit, null if still loading, otherwise returns
   * the configured limit so that it can be displayed to the user.
   */
  get cannotShowUnclassifiedInterfaces() {
    if (!this.isClassificationDataReady) {
      return null;
    }
    return this.unclassifiedInterfaceCount > 1000 ? 1000 : false;
  }

  /**
   * Loads and processes the list of all interfaces that are unclassified
   */
  fetchUnclassifiedInterfacesData() {
    if (this.allUnclassifiedInterfacesCollection.hasFetched) {
      return Promise.resolve();
    }

    /**
     * We do NOT show unclassified interfaces if there are more than 1000. Why?
     * - The current UX for unclassified interfaces is not searchable, making it essentially useless when dealing with large numbers of interfaces
     * - More importantly though: we can't convert that many interfaces to InterfaceModels without crashing the tab via OOM.
     * In future we might revisit this, either by adding pagination to IC interfaces or by moving away from Mobx for displaying this data.
     */
    if (this.cannotShowUnclassifiedInterfaces) {
      // Ensure we mark as fetched to avoid getting stuck in a loop (note: this might cause issues if not handled elsewhere!)
      this.allUnclassifiedInterfacesCollection.hasFetched = true;
      return Promise.resolve();
    }
    return this.getUnclassifiedInterfaces().then(interfaces => {
      const decorated = interfaces
      // drop interfaces that belong to devices which have been deleted (TODO this should really be happening in the backend?)
      .filter(intf => this.deviceCollection.get(intf.device_id)).map(intf => ({
        ...intf,
        device_name: this.deviceCollection.deviceIdNameHash[intf.device_id]
      }));
      this.allUnclassifiedInterfacesCollection.set(decorated);
      this.allUnclassifiedInterfacesCollection.hasFetched = true;
    });
  }

  /**
   * Completely clears and resets all collections, caches, and states related to rule classification.
   */
  resetRuleStates() {
    this.isClassificationDataReady = false;
    this.ruleCollection.reset();
    this.deviceCollection.reset();
    this.testResultsDeviceCollection.reset();
    this.ruleState?.reset();
    this.allUnclassifiedInterfacesCollection.reset();
    this.allUnclassifiedInterfacesCollection.hasFetched = false;
  }

  /**
   * Clears selections and active evaluation data related specifically to temporary Rule tests.
   */
  resetRuleTestStates() {
    this.testResultsDeviceCollection.clearSelection();
    this.testResultsInterfaceCollection.reset();
    this.ruleTestState?.reset();
  }
  handleV3TestClassificationResponse(results) {
    this.v3TestResults = results;
    if (results.success) {
      this.testResultsDeviceCollection.each(device => {
        const dc = results.interfaceMatches[device.id];
        if (dc) {
          const totalInterfaces = device.get('interfaceCount') || 1;
          device.safeSet({
            activeRuleMatchCount: dc.matchedCount,
            interfacesClassifiedCount: dc.classifiedInterfaces,
            percentMatched: dc.classifiedInterfaces / totalInterfaces,
            loadingInterfaceResults: false
          });
        } else {
          device.set('loadingInterfaceResults', false);
        }
      });
      this.testResultsDeviceCollection.sortState = {
        field: 'activeRuleMatchCount',
        direction: 'desc'
      };
      this.testResultsDeviceCollection.sort();
    }
  }

  /**
   * Processes an array of interfaces for a single device, hydrating them into an InterfaceCollection.
   * Automatically applies temporary test rule edits to the array if a rule is actively being tested.
   *
   * @param device - The Device Model that owns these interfaces.
   * @param interfaces - The array of interface objects to process.
   */
  handleDeviceInterfacesLoaded(device, interfaces) {
    const interfaceCollection = new InterfaceCollection(interfaces);
    interfaceCollection.hasFetched = true;
    if (this.ruleTestState && this.ruleTestState.rule) {
      const ruleCollection = new RuleCollection(this.ruleCollection.toJS());
      if (this.ruleTestState.rule.isNew) {
        ruleCollection.add([this.ruleTestState.rule.toJS()]);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ruleCollection.att(ruleCollection.size - 1).optimisticId = this.ruleTestState.rule.optimisticId;
      } else {
        const existingRule = ruleCollection.get(this.ruleTestState.rule.id);
        if (existingRule) {
          existingRule.set(this.ruleTestState.rule.toJS());
        }
      }
      interfaceCollection.activeRule = this.ruleTestState.rule;
      interfaceCollection.ruleCollection = ruleCollection;
      interfaceCollection.sortState = {
        field: 'isClassifiedByActiveRule',
        direction: 'desc'
      };
      interfaceCollection.sort();
    }
    interfaceCollection.each(model => model.set('device_id', device.get('id')));
    device.safeSet({
      interfaceCollection
    });
    device.select();
  }
  get isV3Enabled() {
    const permissions = $auth.getActiveUserProperty('permissions');
    return !!(permissions?.icv3?.enabled || permissions?.icv3?.frontend);
  }
  /**
   * Flags the UI to prompt the user that rule changes were made and a new evaluation is pending.
   */
  setRuleEvaluationRequired() {
    if (!this.isV3Enabled) {
      this.isRuleEvaluationRequired = true;
    } else {
      this.refreshAllClassificationData();
    }
  }

  /**
   * Computes whether the UI is currently in a loading state, either because devices
   * are actively fetching, or because a background rule evaluation is incomplete.
   */
  get isLoadingDevicesInterfaces() {
    return this.deviceCollection.isRequestActive('fetching') || !this.ruleState?.complete && !this.ruleState?.evaluating;
  }

  /**
   * Retrieves the default network boundary associated with a given connectivity type.
    * @returns The default network boundary string.
   */
  getDefaultNetworkBoundary(connectivity_type) {
    return this.settings.get(connectivity_type);
  }
  /**
   * Computes the global total sum of valid interfaces across all active devices in the system.
   */
  get totalInterfaces() {
    if (this.isV3Enabled && this.totalValidInterfacesCount) {
      return this.totalValidInterfacesCount;
    }
    if (this.totalInterfaceCount) {
      return this.totalInterfaceCount;
    }
    let total = 0;
    this.manualDeviceCollection.each(model => {
      total += model.get('interfaceCount') || 0;
    });
    return total;
  }

  /**
   * Computes the total number of devices in the system that possess at least one interface.
   */
  get totalDevicesWithInterfaces() {
    if (this.isV3Enabled) {
      return this.deviceCountsCache.filter(dc => !!dc.total_interfaces).length;
    }
    return this.deviceCollection.unfiltered.filter(device => (device.get('interfaceCount') || 0) > 0)?.length ?? 0;
  }

  /**
   * Computes the global total sum of interfaces that have been classified (either by rule or manually).
   */
  get totalClassified() {
    if (this.isV3Enabled && this.totalValidInterfacesCount) {
      return this.totalValidClassifiedCount;
    }
    if (this.totalInterfaceCount) {
      return this.totalInterfaceCount - this.unclassifiedInterfaceCount;
    }
    let total = 0;
    this.manualDeviceCollection.each(model => {
      total += model.get('classifiedCount') || 0;
    });
    return total;
  }

  /**
   * Computes the overall percentage of interfaces across the system that have been successfully classified.
   *
   * @returns Value from 0 to 100.
   */
  get totalPercentClassified() {
    return this.totalInterfaces === 0 ? 0 : this.totalClassified / this.totalInterfaces;
  }

  /**
   * Computes whether the overall system classification percentage has fallen below
   * the threshold warning level (defaults to 0.75 but can vary by company)
   */
  get interfaceClassBelowThreshold() {
    return !!(this.ruleState?.complete && this.totalPercentClassified < $auth.interfaceClassThreshold);
  }

  /**
   * API call to retrieve the lightweight device classification aggregate numbers (totals, classified counts).
   */
  getDeviceInterfaceCounts() {
    return this.isV3Enabled ? api.get('/api/ui/interfaceClassification/devices') : api.get('/api/ui/interfaces/rules/deviceInterfaceCounts');
  }

  /**
   * API call to retrieve the aggregate count of how many interfaces were matched per Rule ID.
   */
  getInterfacesMatchedPerRule() {
    if (this.isV3Enabled) {
      return api.get('/api/ui/interfaceClassification/rules/counts');
    }

    // Legacy Fallback
    return api.get('/api/ui/interfaces/rules/interfacesMatchedPerRule');
  }

  /**
   * API call to extract the flat array of all unclassified interfaces in the system.
   */
  getUnclassifiedInterfaces() {
    return api.get('/api/ui/interfaces/rules/unclassifiedInterfaces');
  }

  /**
   * API call to extract the full detailed interface objects that specifically match a given rule.
   */
  getDetailedRuleInterfaces(ruleId) {
    return api.get(`/api/ui/interfaces/rules/results/${ruleId}`);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "isRuleEvaluationRequired", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "isClassificationDataReady", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isManualClassificationDataReady", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "unclassifiedInterfaceCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isRefreshingCounts", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "totalInterfaceCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "isFetchingActiveRuleInterfaces", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "isFetchingDeviceInterfaces", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "hasLoadedSettings", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "v3TestResults", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "v3IsTesting", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "totalValidInterfacesCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "totalValidClassifiedCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "deviceCountsCache", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "fetchActiveRuleInterfaces", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchActiveRuleInterfaces"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateRuleInterfacesV3", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateRuleInterfacesV3"), _class.prototype), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "updateRuleInterfaces", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return ruleInterfaces => {
      const flattenedInterfaces = ruleInterfaces.map(({
        interface_details,
        device_id
      }) => ({
        ...interface_details,
        device_id,
        device_name: this.deviceCollection.deviceIdNameHash[device_id]
      }));
      this.activeRuleInterfacesCollection.set(flattenedInterfaces);

      // memoize matchCountsByDevice inside the device collection
      const matchCountsByDevice = flattenedInterfaces.reduce((acc, {
        device_id
      }) => {
        acc[device_id] = (acc[device_id] ?? 0) + 1;
        return acc;
      }, {});
      this.testResultsDeviceCollection.each(device => {
        // we need to update all devices to ensure that we have zeroed out data from other rules
        device.set('activeRuleMatchCount', matchCountsByDevice[device.id] || 0);
      });
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "initialize", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initialize"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "completeRefresh", [action], Object.getOwnPropertyDescriptor(_class.prototype, "completeRefresh"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "openRuleUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "openRuleUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshAllClassificationData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "refreshAllClassificationData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateFromTestResults", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateFromTestResults"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "initClassificationCollections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initClassificationCollections"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "excludeNoDescriptionEnabled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "excludeNoDescriptionEnabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "excludeNoIpEnabled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "excludeNoIpEnabled"), _class.prototype), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "initManualClassificationCollections", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return devicesPromise => {
      this.isManualClassificationDataReady = false;
      if (this.isV3Enabled) {
        return Promise.resolve();
      }

      // Fire the classifiedCounts request in parallel with the (shared) devices request rather than
      // waiting on it first, preserving the original parallelism.
      return Promise.all([api.get('/api/ui/interfaces/rules/classifiedCounts'), devicesPromise]).then(([classifiedCounts, devices]) => {
        // @ts-expect-error TODO this will throw an error if devices has no entries!
        this.manualDeviceCollection.set(this.manualDeviceCollection.deserialize(devices));
        Object.keys(classifiedCounts).forEach(deviceId => {
          const device = this.manualDeviceCollection.get(deviceId);
          if (device) {
            device.set('classifiedCount', classifiedCounts[deviceId].classifiedCount || 0);
            device.set('interfaceCount', classifiedCounts[deviceId].interfaceCount || 0);
          }
        });
        this.manualDeviceCollection.sort();
        this.isManualClassificationDataReady = true;
      });
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "updateDeviceInterfaceCounts", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateDeviceInterfaceCounts"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateRuleCounts", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateRuleCounts"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cannotShowUnclassifiedInterfaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cannotShowUnclassifiedInterfaces"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchUnclassifiedInterfacesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchUnclassifiedInterfacesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetRuleStates", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetRuleStates"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetRuleTestStates", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetRuleTestStates"), _class.prototype), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "testClassifyV3", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async (rule, values, singleDeviceId = null) => {
      if (!this.ruleTestState) {
        throw new Error('Tried to test classification before initializing $interfaceClass store!');
      }
      $auth.track('TestedInterfaceClassificationRule', {
        icv3_enabled: true,
        is_new_rule: rule.isNew,
        ...(values.match_clause || {})
      });
      this.v3IsTesting = true;
      this.v3TestResults = null;
      rule.isTested = true;
      let ruleClone = rule;
      if (!rule.isNew) {
        ruleClone = rule.duplicate({
          removeId: false,
          save: false
        });
      }
      ruleClone.safeSet(values);
      this.ruleTestState.rule = ruleClone;
      this.testResultsDeviceCollection.each(device => {
        device.set('loadingInterfaceResults', true);
        device.set('activeRuleMatchCount', 0);
      });
      const testRulePayload = {
        ...rule.toJS(),
        ...values,
        match_clause: {
          ...rule.get('match_clause'),
          ...(values.match_clause || {})
        }
      };
      try {
        this.handleV3TestClassificationResponse(await api.post('/api/ui/interfaceClassification/test', {
          data: {
            rule: testRulePayload,
            singleDeviceId
          }
        }));
      } catch (error) {
        runInAction(() => {
          this.v3TestResults = {
            success: false,
            interfaceMatches: {},
            error: error && typeof error === 'object' && 'message' in error ? error.message : null
          };
          this.testResultsDeviceCollection.each(device => {
            device.set('loadingInterfaceResults', false);
          });
        });
        console.error('Fast Path Error:', error);
      } finally {
        // ensure we clean up test results, whether succeed or fail
        runInAction(() => {
          this.v3IsTesting = false;
        });
      }
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "handleV3TestClassificationResponse", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleV3TestClassificationResponse"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleDeviceInterfacesLoaded", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleDeviceInterfacesLoaded"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isV3Enabled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isV3Enabled"), _class.prototype), _descriptor16 = _applyDecoratedDescriptor(_class.prototype, "toggleDeviceInterfaceDetails", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async device => {
      try {
        if (device.isSelected) {
          this.deviceCollection.clearSelection();
          this.testResultsDeviceCollection.clearSelection();
          // TODO possibly not the correct type?
          const interfaceCollection = device.get('interfaceCollection');
          if (interfaceCollection) {
            interfaceCollection.reset();
            interfaceCollection.ruleCollection = null;
          }
        } else {
          this.isFetchingDeviceInterfaces = true;
          let interfaces;
          if (this.isV3Enabled) {
            if (this.v3TestResults) {
              // v3 API for viewing test rule results
              const res = await api.get(`/api/ui/interfaceClassification/test/devices/${device.id}`, {
                query: {
                  // we do NOT want to send optimistic IDs for test rules, only when editing an existing rule
                  id: this.ruleTestState?.rule?.isNew ? undefined : this.ruleTestState?.rule?.id,
                  position_index: this.ruleTestState?.rule?.get('position_index') || this.ruleTestState?.rule?.get('rank'),
                  placement: this.ruleTestState?.rule?.get('placement')
                }
              });
              interfaces = res.interfaces;
            } else if (this.ruleCollection.singleSelection) {
              // V3 API for viewing the results of a saved rule
              const activeRuleId = this.ruleCollection.singleSelection.id;
              const res = await api.get(`/api/ui/interfaceClassification/rules/${activeRuleId}/devices/${device.id}`);
              interfaces = res.interfaces;
            } else {
              // V3 API for viewing device interfaces from the main devices tab
              const res = await api.get(`/api/ui/interfaceClassification/devices/${device.id}`);
              interfaces = res.interfaces;
            }
          } else {
            // legacy API
            const testResult = this.ruleTestState?.evaluationResults?.find(r => String(r.device_id) === String(device.id));
            interfaces = testResult?.interfaces ? testResult.interfaces : await api.get(`/api/ui/interfaces/rules/results/device/${device.id}`);
          }

          // Hydrate the collection and open the dialog
          this.handleDeviceInterfacesLoaded(device, interfaces);
        }
      } finally {
        this.isFetchingDeviceInterfaces = false;
      }
    };
  }
}), _descriptor17 = _applyDecoratedDescriptor(_class.prototype, "evaluateRules", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.isRuleEvaluationRequired = false;
      if (!this.isV3Enabled) {
        this.deviceCollection.models.forEach(model => model.set({
          loadingInterfaceResults: true
        }));
        this.ruleState?.send({
          persist: true
        });
      } else {
        this.ruleState?.setEvaluationComplete(true);
      }
    };
  }
}), _descriptor18 = _applyDecoratedDescriptor(_class.prototype, "testRule", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (rule, values) => {
      if (!this.ruleTestState) {
        throw new Error('Attempted to test a rule prior to initialization!');
      }
      $auth.track('TestedInterfaceClassificationRule', {
        icv3_enabled: false,
        is_new_rule: rule.isNew,
        ...(values?.match_clause || {})
      });

      // The rule has been tested, we toggle some UI state based on this in the form.
      rule.isTested = true;

      // Take a clone of the rule and persist the temporary tested values but only if it's not new.
      // If it's new, there's no original state to maintain and it loses its optimistic ID so things don't match.
      let ruleClone = rule;
      if (!rule.isNew) {
        ruleClone = rule.duplicate({
          removeId: false,
          save: false
        });
      }
      ruleClone.set(values);
      this.ruleTestState.rule = ruleClone;

      // construct our data to send to the socket, with the values we want to override.
      this.ruleTestState.send({
        persist: false,
        overrideRules: [{
          id: rule.id,
          enabled: true,
          ...values
        }]
      });
    };
  }
}), _descriptor19 = _applyDecoratedDescriptor(_class.prototype, "reorderRule", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (oldIndex = 0, newIndex = 0) => {
      const rule = this.ruleCollection.at(oldIndex);
      // prevent re-ordering of KMRs
      // TODO I don't think this is the right way to check kentik_managed_rule!
      if (rule && !rule.kentik_managed_rule && oldIndex !== newIndex) {
        $auth.track('ReorderedInterfaceClassificationRule', {
          icv3_enabled: this.isV3Enabled,
          moved_from: oldIndex,
          moved_to: newIndex
        });
        if (this.isV3Enabled) {
          this.isRefreshingCounts = true;
        }
        this.ruleCollection.move(oldIndex, newIndex);
        const data = {
          id: rule.id,
          position_index: newIndex - 1
        };
        const currentGeneration = this.openRuleUpdate();
        api.post('/api/ui/interfaces/rules/reorder', {
          data
        }).then(() => {
          this.refreshAllClassificationData(currentGeneration);
        }).catch(() => {
          this.isRefreshingCounts = false;
        });
        if (!this.isV3Enabled) {
          this.setRuleEvaluationRequired();
        }
      }
    };
  }
}), _descriptor20 = _applyDecoratedDescriptor(_class.prototype, "removeRule", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return rule => {
      if (this.isV3Enabled) {
        this.isRefreshingCounts = true;
      }
      const currentGeneration = this.openRuleUpdate();
      return rule.destroy().then(() => {
        if (!this.isV3Enabled) {
          this.ruleCollection.fetch({
            force: true
          });
          if (this.ruleCollection.size === 0) {
            this.evaluateRules();
          } else {
            this.setRuleEvaluationRequired();
          }
        } else {
          this.refreshAllClassificationData(currentGeneration);
        }
      });
    };
  }
}), _descriptor21 = _applyDecoratedDescriptor(_class.prototype, "setActiveTestRule", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return rule => {
      if (!this.ruleTestState || !this.ruleState) {
        throw new Error('Tried to set active test rule with no rule state available!');
      }
      this.ruleTestState.rule = rule.duplicate({
        removeId: false,
        save: false
      });
      this.ruleTestState.evaluationResults = this.ruleState.evaluationResults.slice();
      // copy totalPercentClassified from global to ensure that it is propagated
      this.ruleTestState.totalPercentClassified = this.totalPercentClassified;
    };
  }
}), _descriptor22 = _applyDecoratedDescriptor(_class.prototype, "toggleRuleEnabled", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return rule => {
      const {
        enabled
      } = rule.get();
      rule.save({
        enabled: !enabled
      }, {
        toast: false
      }).then(() => this.setRuleEvaluationRequired());
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "setRuleEvaluationRequired", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setRuleEvaluationRequired"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLoadingDevicesInterfaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLoadingDevicesInterfaces"), _class.prototype), _descriptor23 = _applyDecoratedDescriptor(_class.prototype, "classifyInterfaces", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return data => {
      const currentGeneration = this.openRuleUpdate();
      return api.post('/api/ui/interfaces/classify/manual', {
        data
      }).then(results => {
        $auth.track('ManuallyClassifiedInterfaces', {
          icv3_enabled: this.isV3Enabled,
          interface_count: data.interfaceIds?.length || 0,
          set_connectivity_type: data.connectivity_type,
          set_network_boundary: data.network_boundary,
          set_provider: data.provider
        });
        this.refreshAllClassificationData(currentGeneration);
        return results;
      });
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "totalInterfaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "totalInterfaces"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "totalDevicesWithInterfaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "totalDevicesWithInterfaces"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "totalClassified", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "totalClassified"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "totalPercentClassified", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "totalPercentClassified"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "interfaceClassBelowThreshold", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "interfaceClassBelowThreshold"), _class.prototype), _class);
const $interfaceClass = new InterfaceClassStore();
export default $interfaceClass;